import { apiClient } from "@api/client"

import type { Institution, InstitutionHealth } from "./types"

import type { GradeScale, PeriodType } from "@/constants/academic"
import type { InstitutionKind } from "@/constants/institutions"
import type { EvaluationCalendar, EvaluationPeriodIn } from "@/types"

export interface CreateInstitutionPayload {
  name: string
  kind?: InstitutionKind
  // PRD-38 §FR-9 — optional on create. Omitting `periods_per_cycle` makes the
  // backend apply DEFAULT_PERIODS_PER_TYPE for the chosen type; omitting all
  // three leaves the model defaults (trimestral / 3 / 0-10).
  period_type?: PeriodType
  periods_per_cycle?: number
  grade_scale?: GradeScale
}

// PRD-38 §FR-12. Note what is NOT here: `| null`. These three columns are
// NON-NULL on Institution, so an explicit null is a 422 from the API rather
// than a "clear it" (there is no level above an Institution to inherit from).
// Keeping null out of the type means the compiler stops that call before it
// ever reaches the wire. School's equivalent payload DOES accept null — that
// asymmetry is the whole inheritance model (ADR-0032).
export interface UpdateInstitutionPayload {
  name?: string
  kind?: InstitutionKind
  period_type?: PeriodType
  periods_per_cycle?: number
  grade_scale?: GradeScale
}

// PRD-55 — logo upload. The first multipart call in this app; the pattern
// is ported from apps/frontend/src/api/students.ts::importRoster (FormData
// + a per-call Content-Type override + a longer timeout than the default
// 15s, since a 256 KB body on a school's connection can outlast it).
const LOGO_UPLOAD_TIMEOUT_MS = 60_000

function logoFormData(file: File): FormData {
  const form = new FormData()
  form.append("file", file)
  return form
}

export const institutionsApi = {
  get: (id: string) => apiClient.get<Institution>(`/admin/institutions/${id}`).then((r) => r.data),

  // PRD-56 §FR-7 — queue this institution's weekly cut now (superuser). The
  // Panel has the same call; importing it from there put the whole Panel
  // into the console's first chunk.
  recomputeSnapshot: (id: string) =>
    apiClient
      .post<{ queued: boolean; task_id: string | null }>("/admin/snapshot/recompute", {
        institution_id: id,
      })
      .then((r) => r.data),

  // PRD-56 §FR-10 — superuser only.
  health: (id: string) =>
    apiClient.get<InstitutionHealth>(`/admin/institutions/${id}/health`).then((r) => r.data),

  // `deleted_only=true` surfaces the trash/restore view (platform-superuser
  // only; a non-superuser's trash is empty by construction). PRD-32 §FR-11.
  // `deleted_only=true` is the TRASH view: only soft-deleted rows. It used to
  // send `include_deleted=true`, which returns live AND deleted together — so
  // the trash listed everyone, the deleted rows were indistinguishable, and
  // the only thing the toggle changed was the hint text (2026-09-15).
  // `include_deleted` still exists on the backend for SubjectsPanel, which
  // needs the combined set.
  list: (trashOnly = false) =>
    apiClient
      .get<Institution[]>("/admin/institutions/", {
        params: trashOnly ? { deleted_only: true } : {},
      })
      .then((r) => r.data),

  create: (data: CreateInstitutionPayload) =>
    apiClient.post<Institution>("/admin/institutions/", data).then((r) => r.data),

  update: (id: string, data: UpdateInstitutionPayload) =>
    apiClient.patch<Institution>(`/admin/institutions/${id}`, data).then((r) => r.data),

  // Soft-delete (sets deleted_at; cascades to schools/professors/admins on the
  // backend). PRD-32 §FR-9/FR-10.
  remove: (id: string) => apiClient.delete(`/admin/institutions/${id}`).then((r) => r.data),

  restore: (id: string) =>
    apiClient.post<Institution>(`/admin/institutions/${id}/restore`).then((r) => r.data),

  /** Set the crest every School under this contract inherits. */
  uploadLogo: (id: string, file: File) =>
    apiClient
      .post<Institution>(`/admin/institutions/${id}/logo`, logoFormData(file), {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: LOGO_UPLOAD_TIMEOUT_MS,
      })
      .then((r) => r.data),

  removeLogo: (id: string) =>
    apiClient.delete<Institution>(`/admin/institutions/${id}/logo`).then((r) => r.data),

  // PRD-61 FR-1.5 — the institution-wide evaluation calendar of one school
  // year. With no year, the API picks the one in course
  // (core/academic_calendar.py::current_school_year — the newest year the
  // institution's classrooms use), the same year the health line reports.
  // PUT replaces the whole set for that year (an empty list clears it).
  calendar: (id: string, schoolYear: string | null) =>
    apiClient
      .get<EvaluationCalendar>(`/admin/institutions/${id}/evaluation-calendar`, {
        params: schoolYear ? { school_year: schoolYear } : {},
      })
      .then((r) => r.data),

  saveCalendar: (id: string, schoolYear: string, periods: EvaluationPeriodIn[]) =>
    apiClient
      .put<EvaluationCalendar>(
        `/admin/institutions/${id}/evaluation-calendar`,
        { periods },
        { params: { school_year: schoolYear } },
      )
      .then((r) => r.data),
}
